import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DisabledActionButton } from "@/components/common/disabled-action-button";
import { useJustCreated } from "@/components/common/enrichment-async-hint";
import { photosDiffer } from "@/components/forms/schemas/media";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  getChangedValues,
  getDirtyValues,
  isFormDirty,
  withClearedFields,
} from "@/lib/utils";
import { VenueMergeButton } from "@/pages/venues/components/venue-merge-button";
import { VenuePreview } from "@/pages/venues/components/venue-preview";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";
import { VenueViewType } from "@/pages/venues/schemas/venue";
import { factsPayload, toFactsForm } from "@/pages/venues/schemas/venue-facts";
import {
  VenueEditFormType,
  venueEditSchema,
} from "@/pages/venues/schemas/venue.edit.form";

// Extend Day.js with the plugin
dayjs.extend(customParseFormat);

const schemaProvider = new ZodProvider(venueEditSchema);

/**
 * Fields a curator may clear, i.e. the ones the API accepts `""` for.
 * `UpdateVenueCommandValidator` only checks `Id` and `Sections`, and
 * `UpdateVenueCommandHandler` `Set()`s each of these straight through to
 * Firestore. Everything else is deliberately absent: `status` is an enum
 * (`VenueStatusDb?` — `""` fails JSON binding → 400), `mapsUrl`/`address`/
 * `timezone`/`name` are non-nullable in the schema, and the nested objects
 * already round-trip.
 */
const CLEARABLE_FIELDS = [
  "phoneNumber",
  "about",
  "area",
  "type",
  // The picker's clear: the server sets the area text from it.
  "areaId",
] as const;

/**
 * The stored venue as form defaults: the opening hours and accessibility are
 * reshaped for their editors (no `source`/`updatedAt`, flags as unknown/yes/no),
 * so an untouched form diffs clean and sends neither.
 */
const toEditDefaults = (venue: VenueViewType): VenueEditFormType =>
  ({
    ...venue,
    ...toFactsForm(venue),
    // Only the key the form edits: the rest of `metadata` is server-derived.
    metadata: { wikidataId: venue.metadata?.wikidataId ?? undefined },
  }) as unknown as VenueEditFormType;

/**
 * `metadata.wikidataId`, diffed from the raw form values (AutoForm drops an emptied field, and the whole
 * `metadata` object always differs from the stored one). The API writes it as the dotted path
 * `metadata.wikidataId`, so a PUT carrying just this key keeps googleMapsCid, tina and the rest.
 * `""` clears it.
 */
const wikidataPayload = (
  raw: Record<string, unknown>,
  stored: VenueViewType | undefined,
): { metadata?: { wikidataId: string } } => {
  const now = String(
    (raw.metadata as { wikidataId?: string | null } | undefined)?.wikidataId ??
      "",
  );
  const before = stored?.metadata?.wikidataId ?? "";
  return now === before ? {} : { metadata: { wikidataId: now } };
};

export const VenuesEdit = () => {
  const { get, update, getUsageCount } = useVenueRepository();
  const { id } = useParsed();
  const { list } = useNavigation();
  const [usage, setUsage] = useState<{ events: number }>();
  const [values, setValues] = useState<VenueViewType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const justCreated = useJustCreated();

  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    get(id!.toString())
      .then((response) => {
        setValues(response);
        setTitle("Edit " + response.name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get venue",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    getUsageCount(id.toString())
      .then(setUsage)
      .catch((error) =>
        console.error("Failed to get venue usage count", error),
      );
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<VenueEditFormType, any, VenueEditFormType>,
  ) => {
    form.watch((changedValues) => {
      if (!values) return;
      const changed = getChangedValues(changedValues, toEditDefaults(values));
      console.debug("Form Changed", changed);
      setWarnWhen(Object.keys(changed).length > 0);
    });
  };

  const onSubmit = async (
    allValues: VenueEditFormType,
    form: UseFormReturn<VenueEditFormType, any, VenueEditFormType>,
  ) => {
    const changed = getChangedValues(
      allValues,
      form.formState.defaultValues,
    ) as Partial<VenueEditFormType>;
    // AutoForm strips emptied fields before we get here — re-add the ones the
    // curator cleared on purpose as "", which the API stores as an empty value.
    // Opening hours + accessibility are diffed on their own, from the raw values:
    // the API marks whatever it receives as the curator's, so only a real change goes.
    const {
      openingHours: _openingHours,
      accessibility: _accessibility,
      metadata: _metadata,
      ...cleared
    } = withClearedFields<VenueEditFormType>(
      changed,
      form.getValues() as Record<string, unknown>,
      form.formState.defaultValues as Record<string, unknown>,
      CLEARABLE_FIELDS,
    ) as Partial<VenueEditFormType>;
    const dirty: typeof cleared & { hasPlaceholderArt?: boolean } = cleared;
    const facts = factsPayload(
      form.getValues() as Record<string, unknown>,
      toFactsForm(values ?? {}),
    );
    if (photosDiffer(values?.media?.photos, allValues.media?.photos)) {
      dirty.hasPlaceholderArt = false;
    }
    setWarnWhen(false);
    const wikidata = wikidataPayload(
      form.getValues() as Record<string, unknown>,
      values,
    );
    await update({ id: allValues.id, ...dirty, ...facts, ...wikidata });
  };

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        {values && (
          <AutoForm
            key={JSON.stringify(values)}
            schema={schemaProvider}
            defaultValues={toEditDefaults(values)}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents()}
          >
            <BottomActionBar className="justify-between">
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
              {usage &&
                values &&
                (usage.events > 0 ? (
                  (values.followers ?? 0) > 0 ? (
                    <DisabledActionButton
                      label="Merge"
                      reason="Cannot merge a venue with followers"
                    />
                  ) : (
                    <VenueMergeButton id={id!.toString()} />
                  )
                ) : (values.followers ?? 0) > 0 ? (
                  <DisabledActionButton
                    label="Delete"
                    reason="Cannot delete a venue with followers"
                  />
                ) : (
                  <DeleteButton
                    resource="venues"
                    onSuccess={() => {
                      list("venues");
                    }}
                  />
                ))}
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
      {values && <VenuePreview venue={values} justCreated={justCreated} />}
    </div>
  );
};
